import {
  ConstantBackoff,
  WebsocketBuilder,
} from '@atlas/collaboration/websocket';
import { createWebsocketStateSignal } from '@atlas/collaboration/websocket/solid/state-signal';
import { SERVER_HOSTS } from '@core/constant/servers';
import { createSessionWebsocketTelemetry } from '../session-websocket-telemetry';
import { runWebSocketJob, type WebSocketJobConfig } from './websocket-job';

/**
 * The interval in milliseconds to send a heartbeat message to the server.
 * We set this to 5 minutes (API Gateway has a 10 minute idle timeout, this will also keep the lambda warm).
 */
const HEARTBEAT_INTERVAL = 300000;

/**
 * The time in milliseconds to wait for a heartbeat response from the server after the heartbeat is sent.
 */
const HEARTBEAT_TIMEOUT = 5000;

export const storageSessionTelemetry = createSessionWebsocketTelemetry(
  'storage',
  () => SERVER_HOSTS['websocket-service']
);

const ws = new WebsocketBuilder(storageSessionTelemetry.resolveUrl)
  .withAutoConnect(false)
  .withBackoff(new ConstantBackoff(2_000))
  .withMaxRetries(20)
  .onOpen(storageSessionTelemetry.opened)
  .onError(storageSessionTelemetry.failed)
  .onClose((_, event) => storageSessionTelemetry.closed(event))
  .onRetry((_, event) => storageSessionTelemetry.retry(event.detail))
  .withHeartbeat({
    pingMessage: JSON.stringify({ action: 'wsping' }),
    pongMessage: 'pong',
    timeout: HEARTBEAT_TIMEOUT,
    interval: HEARTBEAT_INTERVAL,
    maxMissedHeartbeats: 3,
  })
  .build();

export const storageWS = ws;

function reconnectIfDisconnected() {
  storageWS.reconnectIfDisconnected();
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    reconnectIfDisconnected();
  }
}

// When the browser regains connectivity or a background tab becomes visible,
// kick the connection immediately instead of waiting for heartbeat/backoff
// timers, which may have been throttled while the tab was stale.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.addEventListener('online', reconnectIfDisconnected);
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

const _state = createWebsocketStateSignal(ws);

export function createWebSocketJob<T, R, D, U>(
  config: WebSocketJobConfig<T, R, D, U>
): Promise<R> {
  return runWebSocketJob(storageWS, config);
}
